<template>
  <div
    id="video-item-container"
    :class="{ embedded: embed }"
    ref="videoContainer"
    style="position: relative; width: 100%"
    v-if="itemLoaded"
  >
    <videojs-player
      v-if="videoPlayerName === 'videojs' || videoPlayerName === 'hlsjs'"
      ref="videoPlayer"
      @loadedmetadata="onMetadata"
      @ended="onEnded"
      @error="onError"
      @timeupdate="onTimeUpdate"
      @ready="onReady"
      @play="onPlay"
      class="
        w-100
        h-100
        vjs-default-skin vjs-big-play-centered vjs-show-big-play-button-on-pause
      "
      :options="playerOptions"
      :tech="videoPlayerName"
    />

    <shaka-player
      v-else-if="videoPlayerName === 'shaka'"
      ref="videoPlayer"
      @loadedmetadata="onMetadata"
      @ended="onEnded"
      @error="onError"
      :item="item"
      @timeupdate="onTimeUpdate"
      @ready="onReady"
      @play="onPlay"
      class="w-100 h-100"
      :options="playerOptions"
    />

    <hlsjs-player
      v-else-if="videoPlayerName === 'hlsjsdirect'"
      ref="videoPlayer"
      class="w-100 h-100"
      :native="false"
      :src="contentURL"
    />

    <video-end-action :video-ended="ended" :item="item" />
  </div>
</template>

<script>
import iconMute from "@img/icon-muted.svg";
import videojsPlayer from "../videojs_player.vue";
import shakaPlayer from "../shaka_player.vue";
import hlsjsPlayer from "../hlsjs_player.vue";
import VideoEndAction from "./video_end_action.vue";
import UAParser from "ua-parser-js";
import api from "@/src/api.js";
import featureKey from "../../feature_key.macro";

export default {
  props: {
    id: String,
    item: Object,
    statid: String,
    cta: Object,
    embed: Boolean,
  },
  data() {
    return {
      iconMute: iconMute,
      resetPosition: true,
      ended: false,
      pc_reported: 0,
      pc_increment: 0,
      playerOptions: {
        // videojs options
        muted: this.autoplay,
        autoplay: this.autoplay,
        poster: this.posterURL,
        language: "en",
        sources: [
          {
            src: this.item.links.content_url
          },
        ],
        controls: true,
        liveui: true,
        errorDisplay: false,
        preload: "none", // a poster will stop "auto" preload
        aspectRatio: this.embed ? undefined : "16:9",
        fluid: !this.embed,
        fill: this.embed,
        playbackRates: [0.5, 1, 1.2, 1.5, 1.7, 2],
        controlBar: {
          pictureInPictureToggle: true,
          children: [
            "playToggle",
            "progressControl",
            "SeekToLive",
            "remainingTimeDisplay",
            "VolumeControl",
            "PlaybackRateMenuButton",
            "pictureInPictureToggle",
            "fullscreenToggle",
          ],
        },
        html5: {
          vhs: {
            nudgeMaxRetry: 5,
            maxLoadingDelay: 5,
            appendErrorMaxRetry: 5,
          },
        },
      },
    };
  },
  components: { videojsPlayer, shakaPlayer, hlsjsPlayer, VideoEndAction },
  computed: {
    embedMode: function() {
      return !!(this.$route.query?.embed);
    },
    isOwner: function() {
      return (this.item.relationships?.user?.data?.id === this.$store.state?.user?.id === true);
    },
    itemLoaded: function() {
      return this.item !== {};
    },
    posterURL: function () {
      return this.item?.links?.poster_url;
    },
    contentURL: function() {
      return this.item?.links?.content_url;
    },
    autoplay: function () {
      if (navigator.userAgent.includes("Slack")) {
        return true;
      } else if (this.embedMode) {
        return this.$route.query?.autoplay !== undefined;
      } else {
        return !!(
          this.$route.query?.autoplay ||
          this.$store?.state?.features?.[featureKey("autoplay")]
        );
      }
    },
    pcIncrement() {
      if (this.pc_increment) {
        return this.pc_increment;
      }
      let player = videojs("#video-item");
      if (!player?.duration()) {
        return NaN;
      }

      let min_interval = 3;
      let max_interval = 30;

      let interval = player.duration() * 0.05;
      let seconds = Math.min(Math.max(interval, min_interval), max_interval);

      return (this.pc_increment = Math.floor(
        (seconds / player.duration()) * 100
      ));
    },
    videoPlayerName: function() {
      if (!(this.$store.state.item?.id && this.$store.state.item?.attributes)) return undefined;
      
      // See https://github.com/cloudapp/dropper_api/pull/2950/files#r681083769
      const browser = UAParser(navigator.userAgent).browser.name;
      const isInstantVideo = this.$store.state.item.attributes.instant_video;
      const isSafari = browser == "Safari";
      const isLightning = this.$store.state.item.attributes.client_version?.match(/^CloudApp\/[789]/i)

      if (isInstantVideo && isSafari && isLightning) return "hlsjsdirect";

      return (this.$route.query?.player || "videojs");
    }
  },
  watch: {
    videoPlayerName: {
      immediate: true,
      handler: function(value) {
        console.debug(`videoPlayerName watcher: ${value}`);
      }
    },
    itemLoaded: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$set(this.playerOptions, 'autoplay', this.autoplay);
          this.$set(this.playerOptions, 'muted', this.autoplay);
          this.$set(this.playerOptions, 'poster', this.posterURL);
        }
      },
    },
  },
  methods: {
    zoomImage() {
      this.$parent.$parent.zoomImage();
    },
    setMinHeightWidth() {
      if (this.$parent.$parent.setMinHeightWidth) {
        this.$parent.$parent.setMinHeightWidth();
      }
    },
    updateItemStat: function (viewed_pc, length) {
      if (!this.isOwner) {
        let stat_id = this.statid;
        let params = {
          itemId: this.item.id,
          params: { stat_id, viewed_pc, length },
        };
        // embed is not using store, this is broken on embeds let's fix in another ticket.
        if (this.$store) {
          this.$store.dispatch("updateStats", params);
        }
      }
    },
    percentViewed: function () {
      let player = videojs("#video-item"); // There is probably a more Vue.js way to do this.

      if (isNaN(player.duration())) {
        return 0;
      }

      let timeRanges = player.played();
      let seconds_viewed = 0;

      for (let i = 0; i < timeRanges.length; ++i) {
        seconds_viewed += timeRanges.end(i) - timeRanges.start(i);
      }

      return (seconds_viewed / player.duration()) * 100;
    },
    onMetadata: function () {
      let player = videojs("#video-item"); // There is probably a more Vue.js way to do this.
      let iconMute = this.iconMute;

      // This is to start in-progress HLS streams at 0:00. Only call it once.
      if (this.resetPosition) {
        player.currentTime(0);
        this.resetPosition = false;
      }

      if (!this.embed && player.muted()) {
        var button = document.createElement("img");
        var playerContainer = this.$refs.videoContainer;

        button.addEventListener("click", function () {
          player.muted(false);
          playerContainer.removeChild(button);
        });

        // The VideoJS icons are here: https://videojs.github.io/font/
        // I'm not 100% sure why we made it into an SVG, but that's what the mute icon is now.
        // If needed, I think changing the tag to an <i> and adding this class should work too:
        //   button.classList.add("vjs-icon-volume-mute");
        button.setAttribute("src", iconMute);
        button.classList.add("icon-muted");
        playerContainer.appendChild(button);
      }
    },
    onTimeUpdate: function () {
      if (!this.isOwner) {
        let player = videojs("#video-item"); // There is probably a more Vue.js way to do this.
        let percentage = this.percentViewed();
        let next_increment = this.pc_reported + this.pcIncrement;
        let report_at_pc = next_increment > 100 ? 100 : next_increment;

        if (percentage > this.pc_reported && percentage >= report_at_pc) {
          // Round down to number divisible by pcIncrement
          let reported = percentage - (percentage % this.pcIncrement);
          this.pc_reported = percentage == 100 ? 100 : reported;
          this.updateItemStat(percentage, player.duration());
        }
      }
    },
    onReady: function () {
      const player = videojs("#video-item");
      if (this.embed && this.autoplay) {
        player.play();
        player.muted(false);
      }
    },
    onEnded: function () {
      this.ended = true;
      this.$emit("videoEnded", true);
    },
    onError: function (ev) {
      const player = videojs("#video-item");
      console.warn(`Player error: ${player.error()}`);

      let item_id = this.item.id;
      let error = player.error();
      let path = this.$root.routes.tracking_error_path();

      api.post(path, {
        event: "unplayable_video_viewed",
        item_id: item_id,
        embed: this.embed,
        error: {
          code: error.code,
          type: error.type,
          message: error.message,
        },
      });
    },
    onPlay: function (ev) {
      this.ended = false;
      this.$emit("videoEnded", false);
    },
  },
};
</script>

<style lang="scss">
.icon-muted {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  color: black;
  opacity: 0.8;
  bottom: 50px;
  right: 50px;
  position: absolute;
  -webkit-text-stroke: 1px black;
  -webkit-text-stroke-color: black;
}
</style>

<style scoped lang="scss">
@import "../../../packs/application.scss";

// This shouldn't have important, but we need to get rid of the id on #video-item-container
// first. A bunch of legacy code depends on it.
.embedded {
  width: 100% !important;
  height: 100% !important;
  max-width: unset !important;
  padding: 0 !important;
}

#video-item-container {
  flex: 1;
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;

  .video-player {
    width: 100%;
    height: 100%;
  }

  #video-item {
    background-color: transparent;
  }

  .vjs-poster {
    background-color: transparent;
  }
}

@media (max-width: 600px) {
  #video-item-container {
    height: auto;
  }
}
</style>
